import { nanoid } from 'nanoid';
import { useEffect, useRef } from 'react';
import { useMap } from 'react-map-gl/mapbox';
import { proxy, subscribe, useSnapshot } from 'valtio';
import { EditorMode } from '~/common/types';
import type { BasicMapStore } from '../..';
import EditorSurface from '../../canvas/EditorSurface';
import { mapState } from '../../Map';

// NOTE: this is ONLY to let other components/widgets know whether
// measure is active or not. It does not control any local state
// or conditional rendering.
export const measureStatus = proxy({ isMeasuring: false });

export default function MeasureSurface({ store }: { store: BasicMapStore }) {
  const container = useRef<HTMLDivElement>(null);
  const { current: map } = useMap();
  const { editorMode } = useSnapshot(store) as typeof store;

  // biome-ignore lint/correctness/useExhaustiveDependencies: legacy
  useEffect(() => {
    if (container.current && map && editorMode !== EditorMode.None) {
      measureStatus.isMeasuring = true;

      const surface = new EditorSurface({
        id: `editor-surface-${nanoid()}`,
        container: container.current,
        map: map.getMap(),
        store,
      });

      const unsubscribe = subscribe(mapState, () => {
        surface.refreshScreenCoords();
      });

      return () => {
        unsubscribe();
        measureStatus.isMeasuring = false;
        surface.destroy();
      };
    }
  }, [editorMode]);

  if (!map || editorMode === EditorMode.None) {
    return null;
  }

  return (
    <div
      ref={container}
      id="measure-surface-container"
      style={{ cursor: '-moz-grab' }}
      // here we have to remove the bottom-left container's positioning attributes
      className="absolute -bottom-14 -left-4 z-10"
    ></div>
  );
}
